

// import { useState, useEffect } from "react";
// import { NavLink, useLocation } from "react-router-dom";
// import { ChevronDown } from "lucide-react";
// import { NAV_SECTIONS } from "@/lib/nav-config";
// import { useAuth } from "@/context/AuthContext";
// import { ROLE_HIERARCHY } from "@/types/common";
// import { cn } from "@/lib/utils";
// import { ScrollArea } from "@/components/ui/scroll-area";
// import { OutrenaLockup } from "@/components/OutrenaLogo";
// import type { NavItem, NavSection } from "@/lib/nav-config";
 
// /* ── Highlighted nav item ─────────────────────────────────────────────────── */
// function HighlightedNavLink({
//   item,
//   onNavigate,
// }: {
//   item: NavItem;
//   onNavigate?: () => void;
// }) {
//   const Icon = item.icon;
//   return (
//     <NavLink
//       to={item.path}
//       end={item.path === "/"}
//       onClick={onNavigate}
//       className={({ isActive }) =>
//         cn(
//           "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
//           isActive
//             ? "bg-primary text-primary-foreground"
//             : "bg-violet-50 text-violet-700 hover:bg-violet-100 hover:text-violet-800 dark:bg-violet-950/40 dark:text-violet-300 dark:hover:bg-violet-900/50 dark:hover:text-violet-200",
//         )
//       }
//     >
//       <Icon className="h-4 w-4 shrink-0" />
//       <span className="truncate">{item.label}</span>
//     </NavLink>
//   );
// }
 
// /* ── Standard nav item ────────────────────────────────────────────────────── */
// function StandardNavLink({
//   item,
//   onNavigate,
// }: {
//   item: NavItem;
//   onNavigate?: () => void;
// }) {
//   const Icon = item.icon;
//   return (
//     <NavLink
//       to={item.path}
//       end={item.path === "/"}
//       onClick={onNavigate}
//       className={({ isActive }) =>
//         cn(
//           "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
//           isActive
//             ? "bg-primary text-primary-foreground"
//             : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
//         )
//       }
//     >
//       <Icon className="h-4 w-4 shrink-0" />
//       <span className="truncate">{item.label}</span>
//     </NavLink>
//   );
// }
 
// /* ── Collapsible section ──────────────────────────────────────────────────── */
// function NavSectionBlock({
//   section,
//   visibleItems,
//   onNavigate,
// }: {
//   section: NavSection;
//   visibleItems: NavItem[];
//   onNavigate?: () => void;
// }) {
//   const location = useLocation();
 
//   // Auto-expand if any child is active
//   const hasActiveChild = visibleItems.some((item) =>
//     item.path === "/"
//       ? location.pathname === "/"
//       : location.pathname.startsWith(item.path),
//   );
 
//   const [open, setOpen] = useState(
//     hasActiveChild ? true : !(section.defaultCollapsed ?? false),
//   );
 
//   // Re-check when location changes (e.g. navigating via quick-action buttons)
//   useEffect(() => {
//     if (hasActiveChild) setOpen(true);
//   }, [hasActiveChild]);
 
//   // "Overview" section (top) is always visible without a toggle header
//   const isOverview = section.id === "top";
 
//   if (isOverview) {
//     return (
//       <div className="space-y-1">
//         {visibleItems.map((item) =>
//           item.highlight ? (
//             <HighlightedNavLink key={item.path} item={item} onNavigate={onNavigate} />
//           ) : (
//             <StandardNavLink key={item.path} item={item} onNavigate={onNavigate} />
//           ),
//         )}
//       </div>
//     );
//   }
 
//   return (
//     <div className="space-y-1">
//       {/* Section header — clickable toggle */}
//       <button
//         type="button"
//         onClick={() => setOpen((o) => !o)}
//         className={cn(
//           "flex w-full items-center justify-between rounded-md px-3 py-1.5",
//           "text-xs font-semibold uppercase tracking-wider",
//           "text-muted-foreground transition-colors hover:text-foreground",
//         )}
//       >
//         <span>{section.label}</span>
//         <div className="flex items-center gap-1.5">
//           {/* Item count badge — matches Next.js reference */}
//           <span
//             className={cn(
//               "rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums",
//               open
//                 ? "bg-muted text-muted-foreground"
//                 : "bg-muted/60 text-muted-foreground/70",
//             )}
//           >
//             {visibleItems.length}
//           </span>
//           <ChevronDown
//             className={cn(
//               "h-3.5 w-3.5 shrink-0 transition-transform duration-200",
//               open ? "rotate-0" : "-rotate-90",
//             )}
//           />
//         </div>
//       </button>
 
//       {/* Items — animate open/close */}
//       {open && (
//         <div className="space-y-0.5 pl-0">
//           {visibleItems.map((item) =>
//             item.highlight ? (
//               <HighlightedNavLink key={item.path} item={item} onNavigate={onNavigate} />
//             ) : (
//               <StandardNavLink key={item.path} item={item} onNavigate={onNavigate} />
//             ),
//           )}
//         </div>
//       )}
//     </div>
//   );
// }
 
// /* ── Sidebar ─────────────────────────────────────────────────────────────── */
// export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
//   const { user } = useAuth();
//   const userLevel = user ? ROLE_HIERARCHY[user.role] : 0;
//   const isSuperAdmin = user?.role === "SUPER_ADMIN";
 
//   // SUPER_ADMIN only sees the Platform section — all other sections belong
//   // to tenant schemas and are irrelevant (and inaccessible) to the platform admin.
//   const sectionsToRender = isSuperAdmin
//     ? NAV_SECTIONS.filter((s) => s.id === "platform")
//     : NAV_SECTIONS;
 
//   return (
//     <aside className="flex h-full w-64 flex-col border-r bg-card">
//       {/* Logo */}
//       <div className="flex h-16 items-center border-b px-5">
//         <OutrenaLockup width={120} />
//       </div>
 
//       {/* Nav sections */}
//       <ScrollArea maxHeightClass="flex-1" className="flex-1 px-3 py-4">
//         <nav className="space-y-2">
//           {sectionsToRender.map((section) => {
//             const visible = section.items.filter(
//               (it) => ROLE_HIERARCHY[it.minimumRole] <= userLevel,
//             );
//             if (visible.length === 0) return null;
 
//             return (
//               <NavSectionBlock
//                 key={section.id}
//                 section={section}
//                 visibleItems={visible}
//                 onNavigate={onNavigate}
//               />
//             );
//           })}
//         </nav>
//       </ScrollArea>
 
//       {/* User footer */}
//       <div className="border-t p-3">
//         <div className="flex items-center gap-2 rounded-md px-2 py-1.5">
//           <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
//             {user?.name?.charAt(0)?.toUpperCase() ?? "U"}
//           </div>
//           <div className="min-w-0 flex-1">
//             <p className="truncate text-xs font-medium leading-tight">
//               {user?.name ?? "User"}
//             </p>
//             <p className="truncate text-[10px] leading-tight text-muted-foreground">
//               {user?.role ?? ""}
//             </p>
//           </div>
//         </div>
//         <p className="mt-2 px-2 text-[10px] text-muted-foreground">
//           v1.0 — AI Sales Co-pilot
//         </p>
//       </div>
//     </aside>
//   );
// }

/**
 * Sidebar.tsx — collapsible section nav with polished active state.
 *
 * UI POLISH changes (all class-string only — zero logic changes):
 *   • Active nav item: left-border indigo pill (Salesforce + Wavefunction hybrid)
 *     replaces full-width bg-primary block. Uses border-l-2 border-primary +
 *     bg-primary/8 + text-primary so the accent color does the work.
 *   • Inactive hover: bg-accent/60 hover:text-accent-foreground (softer).
 *   • Highlighted items (highlight:true): same left-border active treatment;
 *     inactive gets bg-primary/5 text-primary/80 so they feel gently accented
 *     without clashing with the active state.
 *   • Sidebar width: w-64 → w-60 (saves 16px, reduces empty chrome).
 *   • Nav item padding: py-2 → py-1.5 (tighter density, Salesforce style).
 *   • Section label: slightly reduced bottom margin (pb-0.5 instead of pb-1).
 *   • User footer avatar: uses bg-primary (now indigo) automatically.
 *
 * All logic preserved exactly:
 *   • NAV_SECTIONS loop, role filtering (ROLE_HIERARCHY), SUPER_ADMIN gate
 *   • Collapsible sections, auto-expand on active child
 *   • highlight prop routing (HighlightedNavLink / StandardNavLink)
 *   • onNavigate callback, OutrenaLockup, ScrollArea
 *   • NavSectionBlock, section.id === "top" override
 *   • defaultCollapsed from NavSection
 */
import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { NAV_SECTIONS } from "@/lib/nav-config";
import { useAuth } from "@/context/AuthContext";
import { ROLE_HIERARCHY } from "@/types/common";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { OutrenaLockup } from "@/components/OutrenaLogo";
import type { NavItem, NavSection } from "@/lib/nav-config";

/* ── Highlighted nav item ─────────────────────────────────────────────────── */
function HighlightedNavLink({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      end={item.path === "/"}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
          isActive
            ? "border-l-2 border-primary bg-primary/8 text-primary"
            : "border-l-2 border-transparent bg-primary/5 text-primary/80 hover:bg-primary/10 hover:text-primary",
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

/* ── Standard nav item ────────────────────────────────────────────────────── */
function StandardNavLink({
  item,
  onNavigate,
}: {
  item: NavItem;
  onNavigate?: () => void;
}) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.path}
      end={item.path === "/"}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
          isActive
            ? "border-l-2 border-primary bg-primary/8 text-primary"
            : "border-l-2 border-transparent text-muted-foreground hover:bg-accent/60 hover:text-accent-foreground",
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{item.label}</span>
    </NavLink>
  );
}

/* ── Collapsible section ──────────────────────────────────────────────────── */
function NavSectionBlock({
  section,
  visibleItems,
  onNavigate,
}: {
  section: NavSection;
  visibleItems: NavItem[];
  onNavigate?: () => void;
}) {
  const location = useLocation();

  // Auto-expand if any child is active
  const hasActiveChild = visibleItems.some((item) =>
    item.path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(item.path),
  );

  const [open, setOpen] = useState(
    hasActiveChild ? true : !(section.defaultCollapsed ?? false),
  );

  // Re-check when location changes (e.g. navigating via quick-action buttons)
  useEffect(() => {
    if (hasActiveChild) setOpen(true);
  }, [hasActiveChild]);

  // "Overview" section (top) is always visible without a toggle header
  const isOverview = section.id === "top";

  if (isOverview) {
    return (
      <div className="space-y-0.5">
        {visibleItems.map((item) =>
          item.highlight ? (
            <HighlightedNavLink key={item.path} item={item} onNavigate={onNavigate} />
          ) : (
            <StandardNavLink key={item.path} item={item} onNavigate={onNavigate} />
          ),
        )}
      </div>
    );
  }

  return (
    <div className="space-y-0.5">
      {/* Section header — clickable toggle */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex w-full items-center justify-between rounded-md px-3 py-1.5",
          "text-xs font-semibold uppercase tracking-wider",
          "text-muted-foreground transition-colors hover:text-foreground",
        )}
      >
        <span>{section.label}</span>
        <div className="flex items-center gap-1.5">
          {/* Item count badge */}
          <span
            className={cn(
              "rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums",
              open
                ? "bg-muted text-muted-foreground"
                : "bg-muted/60 text-muted-foreground/70",
            )}
          >
            {visibleItems.length}
          </span>
          <ChevronDown
            className={cn(
              "h-3.5 w-3.5 shrink-0 transition-transform duration-200",
              open ? "rotate-0" : "-rotate-90",
            )}
          />
        </div>
      </button>

      {/* Items */}
      {open && (
        <div className="space-y-0.5 pl-0">
          {visibleItems.map((item) =>
            item.highlight ? (
              <HighlightedNavLink key={item.path} item={item} onNavigate={onNavigate} />
            ) : (
              <StandardNavLink key={item.path} item={item} onNavigate={onNavigate} />
            ),
          )}
        </div>
      )}
    </div>
  );
}

/* ── Sidebar ─────────────────────────────────────────────────────────────── */
export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { user } = useAuth();
  const userLevel = user ? ROLE_HIERARCHY[user.role] : 0;
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  // SUPER_ADMIN only sees the Platform section — all other sections belong
  // to tenant schemas and are irrelevant (and inaccessible) to the platform admin.
  const sectionsToRender = isSuperAdmin
    ? NAV_SECTIONS.filter((s) => s.id === "platform")
    : NAV_SECTIONS;

  return (
    <aside className="flex h-full w-60 flex-col border-r bg-card">
      {/* Logo */}
      <div className="flex h-16 items-center border-b px-5">
        {/* <OutrenaLockup width={120} /> */}
        <OutrenaLockup width={180} />
      </div>

      {/* Nav sections */}
      <ScrollArea maxHeightClass="flex-1" className="flex-1 px-2 py-3">
        <nav className="space-y-1">
          {sectionsToRender.map((section) => {
            const visible = section.items.filter(
              (it) => ROLE_HIERARCHY[it.minimumRole] <= userLevel,
            );
            if (visible.length === 0) return null;

            return (
              <NavSectionBlock
                key={section.id}
                section={section}
                visibleItems={visible}
                onNavigate={onNavigate}
              />
            );
          })}
        </nav>
      </ScrollArea>

      {/* User footer */}
      <div className="border-t p-3">
        <div className="flex items-center gap-2 rounded-md px-2 py-1.5">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
            {user?.name?.charAt(0)?.toUpperCase() ?? "U"}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-medium leading-tight">
              {user?.name ?? "User"}
            </p>
            <p className="truncate text-[10px] leading-tight text-muted-foreground">
              {user?.role ?? ""}
            </p>
          </div>
        </div>
        <p className="mt-2 px-2 text-[10px] text-muted-foreground">
          v1.0 — AI Sales Co-pilot
        </p>
      </div>
    </aside>
  );
}
